import { Component, Input, OnInit } from "@angular/core";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { ICameraChannel } from "@app/main/components/videos/models/ICameraChannel";
import { CameraChannelService } from "@app/main/modules/cameras/_services/camera-channels.service";
import { ChannelFlag, DSTOffsetDay, DSTOffsetMonth, DSTOffsetWeek, StreamaxRecordOverwrite } from "@app/main/modules/foundation/_enums/camera-configs/CameraConfigEnums";
import { ICameraDeviceParameterRecord, RecordToProfileComparison } from "@app/main/modules/foundation/_models/_camera-configs/CameraConfigModels";
import { ICameraDeviceParameterProfileSettings, ITriggerMetricsMap, StreamaxCameraProfileService } from "@app/services/camera/streamax-camera-profile-service";
import { TranslationService } from "@app/services/translation.service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest } from "rxjs";
import './streamax-configs-vehicle-summary.component.scss';

@UntilDestroy()
@Component({
    selector: 'streamax-configs-vehicle-summary',
    template: require('./streamax-configs-vehicle-summary.component.html')
})
export class DeviceProfileVehicleSummaryComponent implements OnInit {

    @Input() deviceRecord: ICameraDeviceParameterRecord;
    @Input() deviceProfile: ICameraDeviceParameterProfileSettings;
    @Input() vehicleType: VehicleType;
    @Input() timezones: ITimeZoneInfo[];

    cameraChannels: ICameraChannel[];

    recordToProfileGeneralComparisons: RecordToProfileComparison[] = [];
    recordToProfileTimezoneComparisons: RecordToProfileComparison[] = [];
    recordToProfileAccAlarmComparisons: RecordToProfileComparison[] = [];
    recordToProfileStatusAlarmComparisons: RecordToProfileComparison[] = [];

    acceleratorAlarmType: string;

    accTriggerDefaults: ITriggerMetricsMap;

    constructor(
        private readonly profileService: StreamaxCameraProfileService,
        private readonly translationService: TranslationService,
    private readonly cameraChannelService: CameraChannelService) { }

    ngOnInit(): void {
        this.setupComponent();
    }

    private setupComponent(): void {

        combineLatest([
            this.profileService.getTriggerDefaults(this.vehicleType),
            this.cameraChannelService.getCameraChannels({ VehicleId: this.deviceRecord.VehicleId })
        ]).pipe(untilDestroyed(this)).subscribe(([defaultTriggers, channels]) => {
            this.accTriggerDefaults = defaultTriggers;
            this.cameraChannels = channels;

            this.setupGeneralComparisons();
            this.setupTimeComparisons();
            this.setupAccAlarmComparisons();
            this.setupStatusAlarmComparisons();
        });             
    }

    private setupStatusAlarmComparisons(): void {
        this.recordToProfileStatusAlarmComparisons.push({
            deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.CameraCoveredAlarmSettings.AlarmEnabled,
            profileMetric: this.deviceProfile.CameraCoveredAlarm,
            MetricString: this.translationService.t("StreamaxConfigs.CamCoveredMetricString"),
            InputType: "check"
        });

        if (this.deviceProfile.CameraCoveredAlarm || this.deviceRecord.CameraDeviceParameterSettings.CameraCoveredAlarmSettings.AlarmEnabled) {

            this.deviceRecord.CameraDeviceParameterSettings.ChannelSpecificSettings.forEach(s => {

                const errorString = (this.deviceProfile.CameraCoveredAlarm && !s.CameraConnected && this.cameraChannels.find(c => c.ChannelId === s.ChannelPosition).IsActive) ? this.translationService.t("StreamaxConfigs.CameraOnlyActiveInVeri") : null;

                this.recordToProfileStatusAlarmComparisons.push({
                    deviceMetric: this.isChannelTriggered(this.deviceRecord.CameraDeviceParameterSettings.CameraCoveredAlarmSettings.ChannelTriggersEnabled, s.ChannelPosition),
                    profileMetric: this.deviceProfile.CameraCoveredAlarm && (s.CameraConnected || this.cameraChannels.find(c => c.ChannelId === s.ChannelPosition).IsActive),
                    MetricString: this.translationService.t("StreamaxConfigs.CamCoveredTriggerMetricString", s.ChannelPosition),
                    InputType: "check",
                    ErrorStringOverride: errorString
                })
            });
        }

        this.recordToProfileStatusAlarmComparisons.push({
            deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.VideoLossAlarmSettings.AlarmEnabled,
            profileMetric: this.deviceProfile.VideoLossAlarm,
            MetricString: this.translationService.t("StreamaxConfigs.VideoLossMetricString"),
            InputType: "check"
        });

        if (this.deviceProfile.VideoLossAlarm || this.deviceRecord.CameraDeviceParameterSettings.VideoLossAlarmSettings.AlarmEnabled) {

            this.deviceRecord.CameraDeviceParameterSettings.ChannelSpecificSettings.forEach(s => {

                const errorString = (this.deviceProfile.VideoLossAlarm && !s.CameraConnected && this.cameraChannels.find(c => c.ChannelId === s.ChannelPosition).IsActive) ? this.translationService.t("StreamaxConfigs.CameraOnlyActiveInVeri") : null;

                this.recordToProfileStatusAlarmComparisons.push({
                    deviceMetric: this.isChannelTriggered(this.deviceRecord.CameraDeviceParameterSettings.VideoLossAlarmSettings.ChannelTriggersEnabled, s.ChannelPosition),
                    profileMetric: this.deviceProfile.VideoLossAlarm && (s.CameraConnected || this.cameraChannels.find(c => c.ChannelId === s.ChannelPosition).IsActive),
                    MetricString: this.translationService.t("StreamaxConfigs.VideoLossTriggerMetricString", s.ChannelPosition),
                    InputType: "check",
                    ErrorStringOverride: errorString
                })
            });
        }
    }

    private isChannelTriggered(enabledFlags: ChannelFlag, channelPosition: number): boolean {
        // Create a bitmask for the specific channel position - 1 to match indexing
        const bitmask = 1 << (channelPosition - 1);
        
        return (enabledFlags & bitmask) === bitmask;
    }

    private setupAccAlarmComparisons(): void {

        if (!!this.deviceRecord.CameraDeviceParameterSettings.Acc1AlarmSettings) {
            this.acceleratorAlarmType = "Acc1"

            this.recordToProfileAccAlarmComparisons.push({
                deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Acc1AlarmSettings.AlarmEnabled,
                profileMetric: this.deviceProfile.AlarmProfileMap.AlarmEnabled,
                MetricString: this.translationService.t("StreamaxConfigs.AccAlarmGlobalSwitchMetricString"),
                InputType: "check"
            });

            if (this.deviceProfile.AlarmProfileMap.AlarmEnabled || this.deviceRecord.CameraDeviceParameterSettings.Acc1AlarmSettings.AlarmEnabled) {

                const customTriggers = this.deviceProfile.AlarmProfileMap.TriggerMetricOverrides.find(o => o.VehicleType === this.vehicleType);

                const comparisonTriggers = customTriggers == undefined ? this.accTriggerDefaults : customTriggers.TriggerMetricsMap;

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Acc1AlarmSettings.XTrigger,
                    profileMetric: comparisonTriggers.GyroTriggers.XTrigger,
                    MetricString: this.translationService.t("StreamaxConfigs.AccAlarmXTriggerMetricString"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Acc1AlarmSettings.YTrigger,
                    profileMetric: comparisonTriggers.GyroTriggers.YTrigger,
                    MetricString: this.translationService.t("StreamaxConfigs.AccAlarmYTriggerMetricString"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Acc1AlarmSettings.ZTrigger,
                    profileMetric: comparisonTriggers.GyroTriggers.ZTrigger,
                    MetricString: this.translationService.t("StreamaxConfigs.AccAlarmZTriggerMetricString"),
                    InputType: "number"
                });
            }
        }

        if (!!this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings) {
            this.acceleratorAlarmType = "Accel2"

            this.recordToProfileAccAlarmComparisons.push({
                deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.AlarmEnabled,
                profileMetric: this.deviceProfile.AlarmProfileMap.AlarmEnabled,
                MetricString: this.translationService.t("StreamaxConfigs.AccAlarmGlobalSwitchMetricString"),
                InputType: "check"
            });

            if (this.deviceProfile.AlarmProfileMap.AlarmEnabled || this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.AlarmEnabled) {

                const customTriggers = this.deviceProfile.AlarmProfileMap.TriggerMetricOverrides.find(o => o.VehicleType === this.vehicleType);

                const comparisonTriggers = customTriggers == undefined ? this.accTriggerDefaults : customTriggers.TriggerMetricsMap;

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HardAccelerationTriggerSettings.TriggerEnabled,
                    profileMetric: this.deviceProfile.AlarmProfileMap.HardAccelerationTriggerEnabled,
                    MetricString: this.translationService.t("StreamaxConfigs.HardAccTriggerVal"),
                    InputType: "check"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HardAccelerationTriggerSettings.StartSpeed,
                    profileMetric: comparisonTriggers.HarshAccelerationTriggerMetrics.StartSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardAccSpeedStart"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HardAccelerationTriggerSettings.EndSpeed,
                    profileMetric: comparisonTriggers.HarshAccelerationTriggerMetrics.EndSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardAccSpeedEnd"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HardAccelerationTriggerSettings.Offset / 1000,
                    profileMetric: comparisonTriggers.HarshAccelerationTriggerMetrics.Offset,
                    MetricString: this.translationService.t("StreamaxConfigs.HardAccOffset"),
                    InputType: "number"
                });
                
                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshBrakingTriggerSettings.TriggerEnabled,
                    profileMetric: this.deviceProfile.AlarmProfileMap.HarshBrakingTriggerEnabled,
                    MetricString: this.translationService.t("StreamaxConfigs.HardBrakeTriggerVal"),
                    InputType: "check"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshBrakingTriggerSettings.StartSpeed,
                    profileMetric: comparisonTriggers.HarshBrakingTriggerMetrics.StartSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardBrakeSpeedStart"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshBrakingTriggerSettings.EndSpeed,
                    profileMetric: comparisonTriggers.HarshBrakingTriggerMetrics.EndSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardBrakeSpeedEnd"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshBrakingTriggerSettings.Offset / 1000,
                    profileMetric: comparisonTriggers.HarshBrakingTriggerMetrics.Offset,
                    MetricString: this.translationService.t("StreamaxConfigs.HardBrakeOffset"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshLeftTurnTriggerSettings.TriggerEnabled,
                    profileMetric: this.deviceProfile.AlarmProfileMap.HarshLeftTurnTriggerEnabled,
                    MetricString: this.translationService.t("StreamaxConfigs.HardLeftTriggerVal"),
                    InputType: "check"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshLeftTurnTriggerSettings.StartSpeed,
                    profileMetric: comparisonTriggers.LeftTurnTriggerMappings.StartSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardLeftSpeedStart"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshLeftTurnTriggerSettings.EndSpeed,
                    profileMetric: comparisonTriggers.LeftTurnTriggerMappings.EndSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardLeftSpeedEnd"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshLeftTurnTriggerSettings.Offset / 1000,
                    profileMetric: comparisonTriggers.LeftTurnTriggerMappings.Offset,
                    MetricString: this.translationService.t("StreamaxConfigs.HardLeftOffset"),
                    InputType: "number"
                });
                
                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshRightTurnTriggerSettings.TriggerEnabled,
                    profileMetric: this.deviceProfile.AlarmProfileMap.HarshRightTurnTriggerEnabled,
                    MetricString: this.translationService.t("StreamaxConfigs.HardRightTriggerVal"),
                    InputType: "check"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshRightTurnTriggerSettings.StartSpeed,
                    profileMetric: comparisonTriggers.RightTurnTriggerMappings.StartSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardRightSpeedStart"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshRightTurnTriggerSettings.EndSpeed,
                    profileMetric: comparisonTriggers.RightTurnTriggerMappings.EndSpeed,
                    MetricString: this.translationService.t("StreamaxConfigs.HardRightSpeedEnd"),
                    InputType: "number"
                });

                this.recordToProfileAccAlarmComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.Accel2AlarmSettings.HarshRightTurnTriggerSettings.Offset / 1000,
                    profileMetric: comparisonTriggers.RightTurnTriggerMappings.Offset,
                    MetricString: this.translationService.t("StreamaxConfigs.HardRightOffset"),
                    InputType: "number"
                });
            }
        }
    }

    private setupGeneralComparisons(): void {
        this.deviceRecord.CameraDeviceParameterSettings.ChannelSpecificSettings.forEach(s => {

            const errorString = (!s.CameraConnected && s.AudioEnabled != this.deviceProfile.AudioEnabled) ? this.translationService.t("StreamaxConfigs.AudioEnabledNoFeed") : null;

            this.recordToProfileGeneralComparisons.push({
                deviceMetric: s.AudioEnabled,
                profileMetric: this.deviceProfile.AudioEnabled,
                MetricString: this.translationService.t("StreamaxConfigs.ChannelNAudio", s.ChannelPosition),
                InputType: "check",
                ErrorStringOverride: errorString
            })
        });

        this.recordToProfileGeneralComparisons.push({
            deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.IgnitionDelaySeconds,
            profileMetric: this.deviceProfile.IgnitionDelay,
            MetricString: this.translationService.t("StreamaxConfigs.IgnitionDelay"),
            InputType: "number"
        });

        this.recordToProfileGeneralComparisons.push({
            deviceMetric: StreamaxRecordOverwrite[this.deviceRecord.CameraDeviceParameterSettings.RecordOverwrite],
            profileMetric: StreamaxRecordOverwrite[this.deviceProfile.RecordOverwrite],
            MetricString: this.translationService.t("StreamaxConfigs.RecordOverwrite"),
            InputType: "string"
        });
    }

    private setupTimeComparisons(): void {

        combineLatest([
            this.profileService.convertStreamaxTimezoneId(this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.TimezoneId),
            this.profileService.convertStreamaxTimezoneId(this.deviceProfile.TimeSettings.TimezoneId)
        ]).pipe(untilDestroyed(this)).subscribe({
            next: ([deviceTimezoneInfoId, profileTimezoneInfoId]) => {
                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.timezones.find(t => t.Id === deviceTimezoneInfoId.timezoneId).DisplayName,
                    profileMetric: this.timezones.find(t => t.Id === profileTimezoneInfoId.timezoneId).DisplayName,
                    MetricString: this.translationService.t("StreamaxConfigs.TimezoneId"),
                    InputType: "string"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.DSTEnabled,
                    profileMetric: this.deviceProfile.TimeSettings.DSTEnabled,
                    MetricString: this.translationService.t("StreamaxConfigs.DstEnabled"),
                    InputType: "check"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.translationService.tEnum(DSTOffsetMonth, "DSTOffsetMonth", this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetStartMonth),
                    profileMetric: this.translationService.tEnum(DSTOffsetMonth, "DSTOffsetMonth", this.deviceProfile.TimeSettings.OffSetStartMonth),
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetStartMonth"),
                    InputType: "string"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.translationService.tEnum(DSTOffsetWeek, "DSTOffsetWeek", this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetStartWeek),
                    profileMetric: this.translationService.tEnum(DSTOffsetWeek, "DSTOffsetWeek", this.deviceProfile.TimeSettings.OffSetStartWeek),
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetStartWeek"),
                    InputType: "string"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.translationService.tEnum(DSTOffsetDay, "DSTOffsetDay", this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetStartDay),
                    profileMetric: this.translationService.tEnum(DSTOffsetDay, "DSTOffsetDay", this.deviceProfile.TimeSettings.OffSetStartDay),
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetStartDay"),
                    InputType: "string"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetStartHour,
                    profileMetric: this.deviceProfile.TimeSettings.OffSetStartHour,
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetStartHour"),
                    InputType: "number"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetStartMinute,
                    profileMetric: this.deviceProfile.TimeSettings.OffSetStartMinute,
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetStartMinute"),
                    InputType: "number"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.translationService.tEnum(DSTOffsetMonth, "DSTOffsetMonth", this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetEndMonth),
                    profileMetric: this.translationService.tEnum(DSTOffsetMonth, "DSTOffsetMonth", this.deviceProfile.TimeSettings.OffSetEndMonth),
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetEndMonth"),
                    InputType: "string"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.translationService.tEnum(DSTOffsetWeek, "DSTOffsetWeek", this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetEndWeek),
                    profileMetric: this.translationService.tEnum(DSTOffsetWeek, "DSTOffsetWeek", this.deviceProfile.TimeSettings.OffSetEndWeek),
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetEndWeek"),
                    InputType: "string"
                });

                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.translationService.tEnum(DSTOffsetDay, "DSTOffsetDay", this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetEndDay),
                    profileMetric: this.translationService.tEnum(DSTOffsetDay, "DSTOffsetDay", this.deviceProfile.TimeSettings.OffSetEndDay),
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetEndDay"),
                    InputType: "string"
                });
                                
                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetEndHour,
                    profileMetric: this.deviceProfile.TimeSettings.OffSetEndHour,
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetEndHour"),
                    InputType: "number"
                });
                
                this.recordToProfileTimezoneComparisons.push({
                    deviceMetric: this.deviceRecord.CameraDeviceParameterSettings.LocalTimeSettings.OffSetEndMinute,
                    profileMetric: this.deviceProfile.TimeSettings.OffSetEndMinute,
                    MetricString: this.translationService.t("StreamaxConfigs.TimeOffsetEndMinute"),
                    InputType: "number"
                });
            }
        });
    }
}

